"use client";

import React, { useCallback, useMemo, FC } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import clsx from "clsx";
import { isEmpty } from "lodash";

import { BffComponentType } from "~/bff/ComponentsConfig";
import { FiltersMenu } from "~/bff/types/FiltersMenu";
import { UniversalComponent } from "~/components/universal-component/component";
import { shoppingTypeFilterComponentConfig } from "~/constants/components-configs/shop-type-filter-component-config";
import { Variables } from "~/constants/request";
import { useVariable } from "~/context/variables/hooks/use-variable";
import { replaceCmsVariables } from "~/helpers/replace-cms-variables";
import { getTestAutomationProps } from "~/helpers/test-automation-props";
import { useScrollTop } from "~/hooks/use-scroll-top";
import { COLOR_GREYSCALE_BLACK_8 } from "~/theme/colors";
import { Close24 } from "~/theme/icons/components/close";
import { Filter24 } from "~/theme/icons/components/filter";

import { Filter } from "./components/filter/component";
import { PRODUCTS_FILTERS_CLASSES } from "./constants";
import {
  getCombinedColorFilters,
  getFacetInCms,
  getFilterLabel,
  getFilterType,
  getSelectedFiltersLabels,
  isCountValueEmpty,
} from "./helpers";
import { ProductsFiltersContainer, ProductsFiltersMobileDrawer } from "./styled";
import { FilterTypes, FacetsNameFromCms, ProductsFiltersProps } from "./types";

const DEFAULT_OPEN_FILTERS_COUNT = 3;

export const ProductsFilters: FC<ProductsFiltersProps> = ({
  appliedFilters,
  onFiltersClick,
  filtersData,
  isFiltersOpen,
  onFiltersClose,
  onAllFiltersClear,
  filtersCaptionDesktop,
  filtersCaptionMobile,
  colorPalette,
  productFilterClearAllButtonText,
  shopFilter,
  shopFilterProps,
  numberItems,
}) => {
  const [scrollTop, scrollProps] = useScrollTop();

  const facetsNamesFromCms = useVariable<FacetsNameFromCms[]>(Variables.FACET_LIST);

  const filtersMenu = useVariable<FiltersMenu>(Variables.FILTERS_MENU);
  const filtersMenuTranslations = filtersMenu?.props?.translations;

  const filteredItemsCount = useMemo(() => {
    return Object.values(appliedFilters ?? {}).reduce<number>((acc, currValue) => {
      if (typeof currValue === "number" && typeof acc === "number") {
        acc += currValue;
      }

      return acc;
    }, 0);
  }, [appliedFilters]);

  const handleApplyFilters = useCallback(() => {
    onFiltersClose();
  }, [onFiltersClose]);

  const renderFilters = useCallback(() => {
    return (
      !isEmpty(filtersData) &&
      Object.entries(filtersData)
        .filter(([key, value]) => {
          const facetInCms = getFacetInCms(key, facetsNamesFromCms);
          const label = getFilterLabel(key);
          const type = getFilterType(key, value);

          const hasValues =
            Array.isArray(value) && !!value.length && !isCountValueEmpty(value);

          const isSwatchFilterSelected =
            type === FilterTypes.swatch &&
            appliedFilters &&
            !!Object.keys(appliedFilters).find((key) =>
              key.includes(label.toLocaleLowerCase()),
            );

          const isColorFilterEmpty =
            type === FilterTypes.color &&
            !getCombinedColorFilters(colorPalette, value).length;

          return (
            facetInCms &&
            key !== "price" &&
            !isSwatchFilterSelected &&
            !isColorFilterEmpty &&
            hasValues
          );
        })
        .map(([key, value], index) => {
          const label = getFilterLabel(key);
          const filterLabelFromCms = getFacetInCms(
            key,
            facetsNamesFromCms,
          )?.displayName;
          const selectedFiltersLabels = getSelectedFiltersLabels(
            key,
            appliedFilters,
          );
          const type = getFilterType(key, value);
          const isExpandedByDefault = index < DEFAULT_OPEN_FILTERS_COUNT;

          return (
            <Filter
              key={key}
              name={key}
              isExpanded={isExpandedByDefault}
              filterLabelFromCms={filterLabelFromCms}
              label={label}
              type={type}
              filters={
                type === FilterTypes.color
                  ? getCombinedColorFilters(colorPalette, value)
                  : value
              }
              onClick={onFiltersClick}
              appliedFilters={appliedFilters}
              filterLabels={selectedFiltersLabels}
              shopFilter={shopFilter}
              shopFilterProps={shopFilterProps}
              translations={filtersMenuTranslations}
            />
          );
        })
    );
  }, [
    appliedFilters,
    colorPalette,
    filtersData,
    onFiltersClick,
    shopFilter,
    shopFilterProps,
    facetsNamesFromCms,
    filtersMenuTranslations,
  ]);

  const filtersRC = (
    <>
      <Box
        className={PRODUCTS_FILTERS_CLASSES.filterContentWrapper}
        {...scrollProps}
      >
        <Box className={PRODUCTS_FILTERS_CLASSES.filterTopBar}>
          <Filter24 />
          <Typography
            className={PRODUCTS_FILTERS_CLASSES.filterTitle}
            variant="body1"
          >
            {filtersCaptionDesktop}
          </Typography>
        </Box>
        <Box
          className={PRODUCTS_FILTERS_CLASSES.filterTopBarMobileSticky}
          style={{
            boxShadow:
              scrollTop > 0 ? `0px 2px 8px ${COLOR_GREYSCALE_BLACK_8}` : "none",
          }}
        >
          <Box className={PRODUCTS_FILTERS_CLASSES.filterTopBarMobile}>
            <Box className={PRODUCTS_FILTERS_CLASSES.filterBtnMobile}>
              <IconButton
                onClick={onFiltersClose}
                color="inherit"
                aria-label="close button"
                size="large"
              >
                <Close24 />
              </IconButton>
              <Typography
                className={PRODUCTS_FILTERS_CLASSES.filterTitle}
                variant="h5"
              >
                {filtersCaptionMobile}
              </Typography>
            </Box>
            {filteredItemsCount > 0 && (
              <Button
                variant="text"
                className="body2 underlined"
                onClick={onAllFiltersClear}
                {...getTestAutomationProps("clear-all-filters-mobile-button")}
              >
                {productFilterClearAllButtonText}
              </Button>
            )}
          </Box>
        </Box>
        <UniversalComponent
          props={shopFilterProps}
          component={shopFilter?.component as BffComponentType}
          componentsConfigs={[shoppingTypeFilterComponentConfig]}
        />
        {renderFilters()}
      </Box>
      <Button
        {...getTestAutomationProps("show-items-button")}
        onClick={handleApplyFilters}
        size="large"
        variant="contained"
        className={PRODUCTS_FILTERS_CLASSES.showButton}
      >
        {replaceCmsVariables(filtersMenuTranslations?.showItemsButtonText, {
          numberItems,
        })}
      </Button>
    </>
  );

  return (
    <>
      <ProductsFiltersContainer
        id="products-filters"
        sx={{ display: { xs: "none", md: "block" } }}
      >
        {filtersRC}
      </ProductsFiltersContainer>
      <ProductsFiltersMobileDrawer
        sx={{ display: { xs: "block", md: "none" } }}
        PaperProps={{ className: clsx(PRODUCTS_FILTERS_CLASSES.drawer) }}
        anchor="left"
        open={isFiltersOpen}
        onClose={onFiltersClose}
      >
        <ProductsFiltersContainer id="products-filters">
          {filtersRC}
        </ProductsFiltersContainer>
      </ProductsFiltersMobileDrawer>
    </>
  );
};
